"use client";
import Icon from "./ui/Icon";
import CorePlayer from "./CorePlayer";
import LazyImage from "./ui/LazyImage";
import VolumeControl from "./VolumeControl";
import Logo from "../assets/images/logo-white.png";
import Vector from "../assets/images/MediaLayer.svg";
import { Dispatch, SetStateAction, RefObject } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/Tabs";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

type EpisodeTrack = {
  id: number;
  title: string;
  subtitle: string;
  cover: string;
  src: string;
  durationLabel?: string;
  date?: string;
};

type PlayBarProps = {
  playerRef: RefObject<any>;
  currentTrack: any;
  episodes: EpisodeTrack[];
  isPlaying: boolean;
  onTogglePlay?: () => void;
  onSelectEpisode?: (episodeId: number) => void;
  volume: number;
  setVolume: Dispatch<SetStateAction<number>>;
  progress: number;
  setProgress: (v: number) => void;
  duration: number;
  onSeek?: (ratio: number) => void;
  formatTime: (v: number) => string;
  rewind: () => void;
  forward: () => void;
  nextTrack: () => void;
  prevTrack: () => void;
  onStopPlay?: () => void;
  setDuration: (v: number) => void;
  onToggleCollapsed?: () => void;
  setIsVisible: (v: boolean) => void;
  mobileOpen?: boolean; // 🟢 پروپ جدید اضافه شد
  onTitleClick?: (open: boolean) => void;
};

export default function PlayBar({
  playerRef,
  currentTrack,
  episodes,
  isPlaying,
  volume,
  setVolume,
  progress,
  setProgress,
  duration,
  onSeek,
  formatTime,
  rewind,
  forward,
  nextTrack,
  prevTrack,
  onTogglePlay,
  onStopPlay,
  onSelectEpisode,
  setDuration,
  onToggleCollapsed,
  setIsVisible,
  mobileOpen = false, // 🟢 دریافت استیت پدر
  onTitleClick,
}: PlayBarProps) {
  const isVideoTrack = !!currentTrack?.src?.match(
    /\.(mp4|webm|mov|ogg|m3u8|mpd)(\?|$)/i,
  );

  const [mediaType, setMediaType] = useState<"video" | "podcast">(
    isVideoTrack ? "video" : "podcast",
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [, setIsHalfScreen] = useState(false);
  const [position, setPosition] = useState<"left" | "right">("left");
  const [activeEpisode, setActiveEpisode] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [episodeSheetOpen, setEpisodeSheetOpen] = useState(false);
  const [mobileCoverFullscreen, setMobileCoverFullscreen] = useState(false);
  const [sheetEpisode, setSheetEpisode] = useState<number | null>(null);
  const desktopVideoRef = useRef<HTMLVideoElement | null>(null);
  const mobileVideoRef = useRef<HTMLVideoElement | null>(null);
  const [showPlaybackIcon, setShowPlaybackIcon] = useState(false);

  // 🟢 سینک کردن کلیک تایتل در پدر با باز شدن شیت اصلی موبایل
  useEffect(() => {
    if (mobileOpen) {
      setEpisodeSheetOpen(true);
    }
  }, [mobileOpen]);

  const getActiveVideo = () =>
    desktopVideoRef.current || mobileVideoRef.current;

  useEffect(() => {
    setMediaType(isVideoTrack ? "video" : "podcast");
  }, [currentTrack?.src, isVideoTrack]);

  useEffect(() => {
    if (currentTrack?.id != null) {
      setActiveEpisode(currentTrack.id);
    }
  }, [currentTrack?.id]);

  useEffect(() => {
    if (!episodeSheetOpen) setMobileCoverFullscreen(false);
  }, [episodeSheetOpen]);

  const selectEpisode = (episodeId: number) => {
    setActiveEpisode(episodeId);
    setSheetEpisode(episodeId);
    onSelectEpisode?.(episodeId);
  };

  useEffect(() => {
    const els = [desktopVideoRef.current, mobileVideoRef.current].filter(
      Boolean,
    ) as HTMLVideoElement[];
    if (!isVideoTrack) return;

    let isSubscribed = true;

    els.forEach((el) => {
      if (isPlaying) {
        if (el.readyState >= 2) {
          el.play().catch(() => {});
        } else {
          const playWhenReady = () => {
            if (isSubscribed) {
              el.play().catch(() => {});
            }
            el.removeEventListener("canplay", playWhenReady);
          };
          el.addEventListener("canplay", playWhenReady);
        }
      } else {
        try {
          el.pause();
        } catch {}
      }
    });

    return () => {
      isSubscribed = false;
    };
  }, [isPlaying, isVideoTrack, currentTrack?.src]);

  useEffect(() => {
    const els = [desktopVideoRef.current, mobileVideoRef.current];
    els.forEach((el) => {
      if (!el || !isVideoTrack) return;
      el.volume = Math.min(1, Math.max(0, volume));
      el.muted = volume <= 0;
    });
  }, [volume, isVideoTrack]);

  const seekMedia = (ratio: number) => {
    const clamped = Math.min(1, Math.max(0, ratio));
    if (isVideoTrack) {
      const el = getActiveVideo();
      if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
      el.currentTime = clamped * el.duration;
      setDuration(el.duration);
      setProgress(clamped);
      return;
    }
    onSeek?.(clamped);
    setProgress(clamped);
  };

  const rewindMedia = () => {
    if (isVideoTrack) {
      const el = getActiveVideo();
      if (!el) return;
      el.currentTime = Math.max(0, el.currentTime - 15);
      return;
    }
    rewind();
  };

  const forwardMedia = () => {
    if (isVideoTrack) {
      const el = getActiveVideo();
      if (!el) return;
      const safeDuration = Number.isFinite(el.duration)
        ? el.duration
        : Infinity;
      el.currentTime = Math.min(safeDuration, el.currentTime + 15);
      return;
    }
    forward();
  };

  const openPlayerSheetForEpisode = (episodeId: number) => {
    selectEpisode(episodeId);
    setMobileMenuOpen(false);
    setEpisodeSheetOpen(true);
  };

  const backToEpisodeListSheet = () => {
    setEpisodeSheetOpen(false);
    setMobileMenuOpen(true);
  };

  const MainPlayerBar = () => {
    const safeDuration = duration && Number.isFinite(duration) ? duration : 0;
    const safeProgress = progress && Number.isFinite(progress) ? progress : 0;

    return (
      <div className="w-full">
        <div className="w-full xl:w-[304px]">
          <input
            type="range"
            min={0}
            max={1}
            step="0.01"
            value={safeProgress}
            dir="ltr"
            onChange={(e) => {
              const ratio = parseFloat(e.target.value);
              seekMedia(ratio);
            }}
            className="media-progress w-full"
            style={
              {
                "--progress": `${Math.min(100, Math.max(0, safeProgress * 100))}%`,
              } as CSSProperties
            }
          />
          <div className="flex justify-between text-xs opacity-80">
            <span>{formatTime(safeDuration)}</span>
            <span>{formatTime(safeProgress * safeDuration)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex items-center justify-between xl:gap-x-16">
      <Tabs
        value={isVideoTrack ? "video" : "podcast"}
        className="hidden xl:flex xl:items-end"
      >
        <TabsList className="!h-[46px] bg-black-01 px-1.5 rounded-xl">
          <TabsTrigger
            value="podcast"
            className="xl:px-[14px] !h-[34px] text-white data-[state=active]:bg-green-05 border-none !rounded-[10px]"
          >
            <Icon icon="Podcast" className="size-6" />
          </TabsTrigger>
          <TabsTrigger
            value="video"
            className="!h-[34px] text-white data-[state=active]:bg-green-05 border-none !rounded-[10px]"
          >
            <Icon icon="Video" className="size-[52px]" />
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="video"
          className={`absolute bottom-[100%] left-5 w-[685px] h-[439px] mb-2.5 bg-black transition-all ease-in-out duration-200 rounded-3xl
            ${isFullscreen ? "fixed inset-0 w-full h-full z-0 xl:z-20" : "z-[10]"}
            ${!isFullscreen ? "w-[685px] h-[439px]" : ""}
            ${position === "left" ? "left-0" : "right-0"}
        `}
        >
          <div className="absolute bottom-[5%] left-[5%] z-10 space-x-8 bg-slate-400/20 p-3 rounded-2xl">
            <button
              onClick={() => {
                setIsFullscreen((p) => !p);
                setIsHalfScreen(false);
              }}
            >
              {isFullscreen ? (
                <Icon icon="Minimize" className="w-6" />
              ) : (
                <Icon icon="FullScreen" className="w-6" />
              )}
            </button>
            <button
              onClick={() =>
                setPosition((p) => (p === "left" ? "right" : "left"))
              }
            >
              <Icon icon="PositionState" className="w-10" />
            </button>
          </div>
          <div className="relative flex h-full items-center justify-center">
            {isVideoTrack ? (
              <video
                ref={desktopVideoRef}
                className={`h-full w-full bg-black object-cover ${isFullscreen ? "rounded-none" : "rounded-3xl"} `}
                src={currentTrack.src}
                poster={currentTrack.cover}
                preload="metadata"
                playsInline
                muted={volume <= 0}
                onClick={() => {
                  onTogglePlay?.();
                  setShowPlaybackIcon(true);
                  setTimeout(() => {
                    setShowPlaybackIcon(false);
                  }, 700);
                }}
                onLoadedMetadata={(e) => {
                  setDuration(e.currentTarget.duration);

                  if (isPlaying) {
                    e.currentTarget.play().catch(() => {});
                  }
                }}
                onTimeUpdate={(e) => {
                  const video = e.currentTarget;

                  if (video.duration > 0) {
                    setDuration(video.duration);
                    setProgress(video.currentTime / video.duration);
                  }
                }}
                onEnded={() => {
                  nextTrack();
                }}
              />
            ) : currentTrack?.cover ? (
              <div className="relative h-full w-full overflow-hidden rounded-3xl bg-black">
                <LazyImage
                  src={currentTrack.cover || Logo.src}
                  alt={currentTrack?.title}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white/80">
                  ویدیوی قابل نمایش وجود ندارد
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center border border-white/20 bg-black/50 text-white/70 rounded-3xl">
                ویدیویی برای نمایش موجود نیست
              </div>
            )}
          </div>
          {showPlaybackIcon && (
            <div
              className="
              pointer-events-none
              absolute
              inset-0
              flex
              items-center
              justify-center
              animate-in
              fade-in
              zoom-in-50
              duration-150
            "
            >
              <div
                className="
                flex
                size-20
                items-center
                justify-center
                rounded-full
                bg-black/40
                backdrop-blur-md
                animate-out
                fade-out
                duration-700
              "
              >
                {isPlaying ? (
                  <Icon icon="Play" className="ml-1 size-10 text-white" />
                ) : (
                  <Icon icon="Pause" className="size-10 text-white" />
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="flex items-center w-full pt-2.5 pb-1 xl:gap-x-9">
        <Dialog>
          <DialogTrigger asChild>
            <button className="hidden xl:block hover:scale-[1.1] xl:pb-3.5 transition duration-75 text-[#A5C293] xl:text-white">
              <Icon icon="Menu" className="size-5" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-[1000px] w-[95vw] bg-transparent border-none p-0 shadow-none focus:ring-0">
            <DialogTitle className="sr-only">منوی پخش</DialogTitle>
            <div className="flex flex-col">
              <div className="relative bg-white px-8 py-5 mb-5 rounded-[40px]">
                <DialogClose className="absolute -top-24 left-1/2 -translate-x-1/2 flex size-14 items-center justify-center border-2 border-white bg-[#4A726A] text-white hover:bg-black/20 transition rounded-2xl shadow-xl">
                  <Icon icon="X" className="size-5" />
                </DialogClose>
                <div className="flex items-center justify-between my-5 font-kahroba">
                  <h2 className="text-right text-4xl font-medium text-green-05">
                    جلسات دوشنبه
                  </h2>
                </div>
                <div className="space-y-3 max-h-[50vh] overflow-y-auto no-scrollbar px-9 py-1 rtl">
                  {episodes.map((episode) => {
                    const isActive = activeEpisode === episode.id;
                    return (
                      <div
                        key={episode.id}
                        className="relative flex items-center w-full cursor-pointer"
                        onClick={() => selectEpisode(episode.id)}
                      >
                        <div
                          className={`flex items-center justify-between w-full text-green-05 pr-4 pl-9 py-4 rounded-[24px] transition duration-300 ${isActive ? "bg-green-02.5/40 border border-green-05/50 relative scale-[1.01] z-10 opacity-100" : "bg-none text-green-05 hover:bg-black/5"}`}
                        >
                          <div className="flex text-right gap-x-2.5">
                            <div className="relative size-20 overflow-hidden rounded-2xl">
                              <LazyImage
                                src={episode.cover || Logo.src}
                                alt={episode.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex flex-col items-start justify-center">
                              <span className="text-base font-semibold">
                                {episode.title}
                              </span>
                              <span className="text-xs line-clamp-2 text-green-05/70 mt-1">
                                {episode.subtitle}
                              </span>
                              <span className="text-xs font-normal">
                                {episode.date ?? "تاریخ نامشخص"}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-x-12">
                            {episode.durationLabel && (
                              <div className="flex items-center gap-x-1 text-green-05/60">
                                <Icon icon="CircleTime" className="w-5" />
                                <span className="flex items-center justify-center">
                                  {episode.durationLabel ?? "00:00"}
                                  <p>دقیقه</p>
                                </span>
                              </div>
                            )}
                            <button
                              onClick={onTogglePlay}
                              className={`flex items-center font-medium gap-x-2 px-2 py-2 rounded-xl transition-colors ${isActive ? "bg-green-05 text-white" : "bg-green-05/10 text-green-05/60 hover:bg-green-05 hover:text-white"}`}
                            >
                              <Icon icon="CirclePlay" className="size-5" />
                              <span>پخش</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-between items-center gap-x-8 w-full py-8 px-6 bg-green-05 text-white rounded-[32px]">
                <div className="flex items-center justify-center gap-x-8">
                  <div className="flex items-center">
                    <Tabs
                      value={isVideoTrack ? "video" : "podcast"}
                      className="hidden xl:flex xl:items-end"
                    >
                      <TabsList className="!h-[46px] bg-black-01 px-1.5 rounded-xl">
                        <TabsTrigger
                          value="podcast"
                          className="xl:px-[14px] !h-[34px] data-[state=active]:bg-green-05 text-white border-none !rounded-[10px]"
                        >
                          <Icon icon="Podcast" className="size-6" />
                        </TabsTrigger>
                        <TabsTrigger
                          value="video"
                          className="!h-[34px] data-[state=active]:bg-green-05 text-white border-none !rounded-[10px]"
                        >
                          <Icon icon="Video" className="size-[52px]" />
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  <div className="flex items-center gap-x-8 w-full">
                    <VolumeControl value={volume} onChange={setVolume} />
                    <MainPlayerBar />
                  </div>
                </div>
                <CorePlayer
                  playerRef={playerRef}
                  currentTrack={currentTrack}
                  isPlaying={isPlaying}
                  onTogglePlay={onTogglePlay}
                  onStopPlay={onStopPlay}
                  progress={progress}
                  setProgress={setProgress}
                  duration={duration}
                  setDuration={setDuration}
                  volume={volume}
                  setVolume={setVolume}
                  formatTime={formatTime}
                  rewind={rewindMedia}
                  forward={forwardMedia}
                  nextTrack={nextTrack}
                  prevTrack={prevTrack}
                  onToggleCollapsed={onToggleCollapsed}
                  setIsVisible={setIsVisible}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* 📱 شیت لیست قسمت‌ها در موبایل */}
        <div className="flex items-center justify-center gap-x-4 px-4 pb-2.5 xl:p-0">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button className="xl:hidden hover:scale-[1.1] transition duration-75 z-10 text-[#A5C293]">
                <Icon icon="Menu" className="size-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              showHandle={false}
              className="w-full border-none p-0 min-h-[100%] shadow-none focus:ring-0 data-[side=bottom]:h-[100svh] data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:w-screen data-[side=bottom]:max-w-none rounded-none"
            >
              <div className="relative w-full h-full overflow-hidden text-white rtl">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${currentTrack?.cover})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-complement-05/80 via-[#2F4C4A]/100 via-90% to-[#223F3E]/90" />
                <div className="relative z-30 flex items-center justify-between p-7">
                  <SheetClose asChild>
                    <button
                      type="button"
                      onClick={() => setMobileMenuOpen(false)}
                      className="size-9 border-2 border-white/20 transition flex items-center justify-center rounded-[13px]"
                      title="بستن"
                    >
                      <Icon icon="X" className="size-3 text-white/50" />
                    </button>
                  </SheetClose>

                  <SheetClose asChild>
                    <button
                      type="button"
                      onClick={() => setMobileMenuOpen(false)}
                      className="size-9 border-2 border-white/20 transition flex items-center justify-center rounded-[13px]"
                      title="وضعیت"
                    >
                      <Icon
                        icon="CloseState"
                        className="size-4 text-white/50"
                      />
                    </button>
                  </SheetClose>
                </div>
                <div className="relative z-10 px-6 h-full">
                  <div className="flex items-center justify-start gap-4">
                    <div className="w-16 shrink-0 aspect-square border border-white/25 bg-white/10 overflow-hidden rounded-[19px] flex items-center justify-center">
                      <LazyImage
                        src={currentTrack?.cover || Logo.src}
                        alt="کاور اپیزود"
                        className="h-full w-full object-fill"
                      />
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-white/70">
                        {sheetEpisode ?? activeEpisode} | اردیبهشت ۱۴۰۴
                      </p>
                      <h2 className="text-lg font-bold leading-tight">
                        {currentTrack?.title}
                      </h2>
                      <p className="line-clamp-1 text-xs text-white/75">
                        {currentTrack?.subtitle}
                      </p>
                    </div>
                  </div>
                  <div className="mt-5">
                    <MainPlayerBar />
                  </div>
                  <div className="flex items-center justify-between mt-1.5 px-3">
                    <button
                      onClick={forwardMedia}
                      className="flex hover:scale-110"
                    >
                      <Icon icon="FifNext" className="size-5 opacity-60" />
                    </button>
                    <button
                      onClick={nextTrack}
                      className="size-[45px] hover:scale-110 rounded-[18px]"
                    >
                      <Icon icon="Next" className="size-4 opacity-60" />
                    </button>
                    <button
                      onClick={onTogglePlay}
                      className="flex items-center justify-center w-[35%] bg-complement-05 hover:scale-110 transition text-white size-[45px] rounded-[18px]"
                    >
                      {isPlaying ? (
                        <Icon icon="Pause" className="size-5" />
                      ) : (
                        <Icon icon="Play" className="size-5 pl-0.5" />
                      )}
                    </button>
                    <button
                      onClick={prevTrack}
                      className="size-[45px] hover:scale-110 opacity-60 rounded-[18px]"
                    >
                      <Icon icon="Prev" className="size-4" />
                    </button>
                    <button
                      onClick={rewindMedia}
                      className="flex hover:scale-110 opacity-60"
                    >
                      <Icon icon="FifPrev" className="size-5" />
                    </button>
                  </div>
                  <div className="mt-11">
                    <h3 className="text-sm font-normal text-white text-right">
                      قسمت‌ ها
                    </h3>
                    <div className="max-h-[42vh] overflow-y-auto pr-1 custom-scrollbar py-5">
                      {episodes.map((episode, i) => (
                        <div key={episode.id}>
                          <div
                            className="flex items-start justify-start gap-3 px-3 pt-2 pb-2 transition cursor-pointer rounded-2xl"
                            onClick={() =>
                              openPlayerSheetForEpisode(episode.id)
                            }
                          >
                            <div className="size-12 border border-white/15 bg-white/10 shrink-0 overflow-hidden rounded-[10px]">
                              <LazyImage
                                src={episode.cover || Logo.src}
                                alt="کاور"
                                className="h-full w-full object-contain"
                              />
                            </div>
                            <div className="text-right">
                              <p className="text-[11px] text-white/60">
                                {episode.date ?? "تاریخ نامشخص"}
                              </p>
                              <p className="text-sm font-semibold leading-snug">
                                {episode.title}
                              </p>
                              <p className="text-[11px] text-white/60 line-clamp-1">
                                {episode.subtitle}
                              </p>
                            </div>
                          </div>
                          {i !== episodes.length - 1 && (
                            <div className="w-full h-px bg-white/20 my-1" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <div className="pt-2 xl:pt-0 xl:pb-1">
            <VolumeControl value={volume} onChange={setVolume} />
          </div>
        </div>
        <MainPlayerBar />
      </div>

      {/* 📱 شیت پلیر اختصاصی موبایل (پس از انتخاب قسمت) */}
      <div className="xl:hidden">
        <Sheet
          open={episodeSheetOpen}
          onOpenChange={(isOpen) => {
            setEpisodeSheetOpen(isOpen);
            if (!isOpen) onTitleClick?.(false);
          }}
        >
          <SheetContent
            side="bottom"
            showHandle={false}
            className="border-none bg-transparent p-0 min-h-[100%] shadow-none focus:ring-0 data-[side=bottom]:h-[100svh] data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:w-screen data-[side=bottom]:max-w-none rounded-none"
          >
            <div className="relative h-full w-full overflow-hidden text-white rtl">
              <div className="absolute inset-0 z-0">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${currentTrack?.cover})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-complement-05/80 via-[#2F4C4A]/100 via-90% to-[#223F3E]/90" />

                <div
                  className="pointer-events-none absolute inset-0 bg-center bg-cover opacity-100"
                  style={{
                    backgroundImage: `url(${Vector})`,
                    filter: "brightness(0.6) opacity(0.35)",
                    mixBlendMode: "multiply",
                  }}
                  aria-hidden="true"
                />
              </div>

              <div className="relative z-30 flex items-center justify-between p-7">
                <SheetClose asChild>
                  <button
                    type="button"
                    onClick={() => backToEpisodeListSheet()}
                    className="size-9 border-2 border-white/20 transition flex items-center justify-center rounded-[13px]"
                    title="بستن"
                  >
                    <Icon icon="X" className="size-3 text-white/50" />
                  </button>
                </SheetClose>

                <SheetClose asChild>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setEpisodeSheetOpen(false);
                      onTitleClick?.(false);
                    }}
                    className="size-9 border-2 border-white/20 transition flex items-center justify-center rounded-[13px]"
                    title="وضعیت"
                  >
                    <Icon icon="CloseState" className="size-4 text-white/50" />
                  </button>
                </SheetClose>
              </div>

              <div className="relative z-10 px-6 h-full flex flex-col items-center gap-11 text-center">
                <Tabs
                  value={isVideoTrack ? "video" : "podcast"}
                  className="flex xl:hidden xl:items-end"
                >
                  <TabsList className="flex gap-x-2 !h-[42px] p-1 bg-white/30 rounded-xl pointer-events-none">
                    <TabsTrigger
                      value="podcast"
                      className="w-[50px] shadow-none data-[state=active]:bg-white text-[#6C885A] border-none !rounded-[10px]"
                    >
                      <Icon icon="Podcast" className="size-6" />
                    </TabsTrigger>
                    <TabsTrigger
                      value="video"
                      className="w-[50px] shadow-none data-[state=active]:bg-white text-[#6C885A] border-none !rounded-[10px]"
                    >
                      <Icon icon="Video" className="size-[52px]" />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <div className="relative size-[300px] border border-white/20 bg-black/10 shadow-2xl rounded-[28px] overflow-hidden z-10">
                  {mediaType === "video" && isVideoTrack ? (
                    <video
                      ref={mobileVideoRef}
                      src={currentTrack?.src}
                      poster={currentTrack?.cover || Logo.src}
                      className="w-full h-full object-fill"
                      playsInline
                      onClick={() => {
                        onTogglePlay?.();
                        setShowPlaybackIcon(true);
                        setTimeout(() => {
                          setShowPlaybackIcon(false);
                        }, 700);
                      }}
                      onLoadedMetadata={(e) => {
                        setDuration(e.currentTarget.duration);
                        if (isPlaying) {
                          e.currentTarget.play().catch(() => {});
                        }
                      }}
                      onTimeUpdate={(e) => {
                        const video = e.currentTarget;

                        if (video.duration > 0) {
                          setDuration(video.duration);
                          setProgress(video.currentTime / video.duration);
                        }
                      }}
                      onEnded={() => {
                        nextTrack();
                      }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center relative z-[1]">
                      <LazyImage
                        src={currentTrack?.cover || Logo.src}
                        alt="کاور"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  {showPlaybackIcon && (
                    <div
                      className="
                      pointer-events-none
                      absolute
                      inset-0
                      flex
                      items-center
                      justify-center
                      animate-in
                      fade-in
                      zoom-in-50
                      duration-150
                    "
                    >
                      <div
                        className="
                        flex
                        size-20
                        items-center
                        justify-center
                        rounded-full
                        bg-black/40
                        backdrop-blur-md
                        animate-out
                        fade-out
                        duration-700
                      "
                      >
                        {isPlaying ? (
                          <Icon icon="Pause" className="size-10 text-white" />
                        ) : (
                          <Icon
                            icon="Play"
                            className="ml-1 size-10 text-white"
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {isVideoTrack && (
                    <button
                      type="button"
                      onClick={() => {
                        const videoEl = mobileVideoRef.current;
                        if (!videoEl) return;
                        if (videoEl.requestFullscreen) {
                          videoEl.requestFullscreen();
                        } else if ((videoEl as any).webkitRequestFullscreen) {
                          (videoEl as any).webkitRequestFullscreen();
                        } else if ((videoEl as any).webkitEnterFullscreen) {
                          (videoEl as any).webkitEnterFullscreen();
                        }
                      }}
                      className="absolute bottom-3 left-3 size-9 bg-black/50 backdrop-blur-sm transition rounded-[13px] flex items-center justify-center z-20"
                    >
                      <Icon
                        icon="FullScreen"
                        className="w-4 text-white opacity-80"
                      />
                    </button>
                  )}

                  {mobileCoverFullscreen && (
                    <div className="absolute inset-0 flex flex-col justify-between p-7 bg-gradient-to-b from-black/50 via-transparent to-[#223F3E]">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center justify-center gap-2.5">
                          <div className="!size-14 shrink-0 bg-black/75 transition-all duration-300 overflow-hidden rounded-xl">
                            <LazyImage
                              src={currentTrack?.cover || Logo.src}
                              alt="کاور"
                              className="object-fill"
                            />
                          </div>
                          <div className="text-right text-white">
                            <p className="text-[11px] opacity-70">
                              اردیبهشت ۱۴۰۴
                            </p>
                            <h3 className="text-sm font-bold">
                              {currentTrack?.title}
                            </h3>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setMobileCoverFullscreen(false)}
                          className="size-9 flex items-center justify-center border-2 border-complement-03 rounded-xl"
                        >
                          <Icon icon="X" className="w-3 text-complement-03" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {!mobileCoverFullscreen && (
                  <div className="text-center space-y-0.5 relative z-10">
                    <h2 className="line-clamp-1 text-lg font-bold">
                      {currentTrack?.title}
                    </h2>
                    <p className="line-clamp-1 text-xs text-white/75">
                      {currentTrack?.subtitle}
                    </p>
                    <p className="text-xs text-white/60">
                      {currentTrack?.date}
                    </p>
                  </div>
                )}
              </div>

              <div className="absolute bottom-0 left-0 right-0 z-20 pt-4 space-y-2">
                <div className="flex items-center justify-between px-4">
                  <button
                    type="button"
                    onClick={backToEpisodeListSheet}
                    className="size-9 border-2 border-complement-03 text-complement-03 bg-transparent rounded-xl"
                    title="لیست قسمت ها"
                  >
                    <Icon icon="Menu" className="w-4" />
                  </button>
                  {isVideoTrack && mobileCoverFullscreen && (
                    <button
                      type="button"
                      onClick={() => setMobileCoverFullscreen(false)}
                      className="size-9 flex items-center justify-center border-2 border-complement-03 rounded-xl"
                      title="Minimize"
                    >
                      <Icon
                        icon="Minimize"
                        className="w-4 text-complement-03"
                      />
                    </button>
                  )}
                </div>
                <div className="backdrop-blur-md p-4">
                  <MainPlayerBar />
                  <div className="flex items-center justify-between mt-4 px-3">
                    <button
                      onClick={forwardMedia}
                      className="flex hover:scale-110"
                    >
                      <Icon icon="FifNext" className="size-5 opacity-60" />
                    </button>
                    <button
                      onClick={nextTrack}
                      className="size-[45px] hover:scale-110 rounded-[18px] opacity-60"
                    >
                      <Icon icon="Next" className="size-4" />
                    </button>
                    <button
                      onClick={onTogglePlay}
                      className="flex items-center justify-center w-[35%] bg-complement-05 hover:scale-110 transition text-white size-[45px] rounded-[18px]"
                    >
                      {isPlaying ? (
                        <Icon icon="Pause" className="w-5" />
                      ) : (
                        <Icon icon="Play" className="w-5 pl-0.5" />
                      )}
                    </button>
                    <button
                      onClick={prevTrack}
                      className="size-[45px] hover:scale-110 opacity-60 rounded-[18px]"
                    >
                      <Icon icon="Prev" className="size-4" />
                    </button>

                    <button
                      onClick={rewindMedia}
                      className="flex hover:scale-110 opacity-60"
                    >
                      <Icon icon="FifPrev" className="size-[22px]" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
